import { localize } from "@/i18n/translate";
import { useEffect, useState } from "react";
import { ChevronDown, Loader2, Lock, Trash2 } from "lucide-react";
import { api, errorCode } from "@/lib/api";
import { usePortal } from "../i18n";
const Note = ({
  n,
  onDeleted
}) => {
  const {
    p,
    fmtDate,
    errText
  } = usePortal();
  const [confirm, setConfirm] = useState(false);
  const [err, setErr] = useState(null);
  const remove = async () => {
    if (!confirm) return setConfirm(true);
    try {
      await api.delete(`/admin/notes/${n.id}`);
      onDeleted(n.id);
    } catch (e) {
      setErr(errText(errorCode(e)));
    }
  };
  return <li className="border-l-2 border-ink/20 bg-white/70 p-4" data-testid={`note-${n.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs text-mute"><span className="font-semibold text-ink">{localize(n.author_name || n.author_email)}</span> · {localize(fmtDate(n.created_at))}</p>
        <button type="button" onClick={remove} onBlur={() => setConfirm(false)} className={`inline-flex items-center gap-1 text-xs ${confirm ? "font-medium text-signaldark" : "text-mute hover:text-signal"}`} data-testid={`note-delete-${n.id}`}>
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> {localize(confirm ? p.adm.confirmDelete : p.adm.deleteNote)}
        </button>
      </div>
      <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-ink">{localize(n.body)}</p>
      {localize(err && <p className="mt-1 text-xs text-signaldark">{localize(err)}</p>)}
    </li>;
};
export const ClientNotes = ({
  user
}) => {
  const {
    p,
    errText
  } = usePortal();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(null);
  const [count, setCount] = useState(user.notes_count ?? 0);
  const [body, setBody] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const panelId = `notes-${user.id}`;
  useEffect(() => {
    if (!open || notes) return;
    api.get(`/admin/users/${user.id}/notes`).then(r => setNotes(Array.isArray(r.data) ? r.data : [])).catch(e => {
      setErr(errText(errorCode(e)));
      setNotes([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const add = async e => {
    e.preventDefault();
    if (!body.trim()) return setErr(p.adm.emptyNote);
    setBusy(true);
    setErr(null);
    try {
      const {
        data
      } = await api.post(`/admin/users/${user.id}/notes`, {
        body
      });
      setNotes(prev => [data, ...(prev || [])]);
      setCount(c => c + 1);
      setBody("");
    } catch (ex) {
      setErr(errText(errorCode(ex)));
    } finally {
      setBusy(false);
    }
  };
  const onDeleted = id => {
    setNotes(prev => prev.filter(n => n.id !== id));
    setCount(c => Math.max(0, c - 1));
  };
  return <div className="border-t border-dashed border-ink/15 pt-4">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls={panelId} className="inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-signal" data-testid={`notes-toggle-${user.email}`}>
        <Lock className="h-4 w-4" aria-hidden="true" /> {localize(p.adm.notes)}
        <span className="font-mono text-xs text-mute" data-testid={`notes-count-${user.email}`}>({localize(count)})</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {localize(open && <div id={panelId} className="mt-4 max-w-2xl space-y-4" data-testid={`notes-panel-${user.email}`}>
          <p className="text-xs text-mute">{localize(p.adm.notesHint)}</p>
          <form onSubmit={add} noValidate className="space-y-3">
            <label htmlFor={`${panelId}-body`} className="sr-only">{localize(p.adm.notePh)}</label>
            <textarea id={`${panelId}-body`} rows={3} maxLength={2000} value={body} onChange={e => setBody(e.target.value)} placeholder={localize(p.adm.notePh)} className="block w-full resize-y border border-ink/25 bg-white px-3 py-2 text-sm text-ink placeholder:text-zinc-500 focus:border-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-signal" data-testid={`note-input-${user.email}`} />
            {localize(err && <p role="alert" className="text-xs text-signaldark">{localize(err)}</p>)}
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-ink px-4 py-2 text-sm font-medium text-paper hover:bg-signal disabled:opacity-60" data-testid={`note-save-${user.email}`}>
              {localize(busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />)} {localize(p.adm.addNote)}
            </button>
          </form>
          {localize(notes === null ? <Loader2 className="h-4 w-4 animate-spin text-mute" aria-hidden="true" /> : notes.length === 0 ? <p className="text-sm text-mute">{localize(p.adm.noNotes)}</p> : <ul className="space-y-2">{localize(notes.map(n => <Note key={n.id} n={n} onDeleted={onDeleted} />))}</ul>)}
        </div>)}
    </div>;
};
